import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  LayoutDashboard, Truck, Package, Users, DollarSign, MapPin, ChevronRight,
  Loader2, UserPlus, Clock, CheckCircle, AlertTriangle, Play, Check, X, RefreshCw,
  Mail, Link as LinkIcon, Copy, ExternalLink, FileText, Map
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import DriverLocationMap from '../components/DriverLocationMap';
import SOSAlertPanel from '../components/SOSAlertPanel';

const DispatchBoardPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loads, setLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unassigned, assigned, in_transit
  const [activeTab, setActiveTab] = useState('loads'); // loads, map
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [sendingTracking, setSendingTracking] = useState(false);
  const [shipperEmail, setShipperEmail] = useState('');
  const [shipperName, setShipperName] = useState('');
  const [sendDriverInvite, setSendDriverInvite] = useState(true); // Send driver app invite

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [loadsRes, driversRes, statsRes] = await Promise.all([
        api.get('/dispatch/loads'),
        api.get('/drivers'),
        api.get('/dispatch/stats')
      ]);
      setLoads(loadsRes.data);
      setDrivers(driversRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch dispatch data:', error);
      toast.error('Failed to load dispatch data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredLoads = () => {
    switch (filter) {
      case 'unassigned':
        return loads.filter(l => !l.assigned_driver_id && l.status === 'posted');
      case 'assigned':
        return loads.filter(l => l.assigned_driver_id && l.assignment_status === 'assigned');
      case 'in_transit':
        return loads.filter(l => ['picked_up', 'in_transit'].includes(l.assignment_status));
      case 'delivered':
        return loads.filter(l => l.status === 'delivered');
      default:
        return loads;
    }
  };

  const handleOpenAssignDialog = (load) => {
    setSelectedLoad(load);
    setSelectedDriver('');
    setShowAssignDialog(true);
  };

  const handleAssignDriver = async () => {
    if (!selectedDriver) {
      toast.error('Please select a driver');
      return;
    }

    setAssigning(true);
    try {
      const response = await api.post(`/dispatch/loads/${selectedLoad.id}/assign`, {
        driver_id: selectedDriver,
        send_invite: sendDriverInvite
      });
      
      if (response.data.invite_sent) {
        toast.success('Driver assigned & app invite sent!');
      } else {
        toast.success('Driver assigned successfully');
      }
      
      setShowAssignDialog(false);
      setSendDriverInvite(true); // Reset for next time
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    } finally {
      setAssigning(false);
    }
  };

  const handleUpdateStatus = async (loadId, status) => {
    try {
      await api.put(`/dispatch/loads/${loadId}/status`, { status });
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUnassign = async (loadId) => {
    if (!window.confirm('Remove driver assignment from this load?')) return;
    
    try {
      await api.delete(`/dispatch/loads/${loadId}/unassign`);
      toast.success('Driver unassigned');
      fetchData();
    } catch (error) {
      toast.error('Failed to unassign driver');
    }
  };

  const handleOpenTrackingDialog = async (load) => {
    setSelectedLoad(load);
    setShipperEmail('');
    setShipperName('');
    setTrackingInfo(null);
    
    // Check if tracking already exists
    try {
      const response = await api.get(`/tracking/load/${load.id}`);
      if (response.data.has_tracking) {
        setTrackingInfo(response.data);
      }
    } catch (error) {
      // No tracking exists yet
    }
    
    setShowTrackingDialog(true);
  };

  const handleSendTracking = async () => {
    if (!shipperEmail) {
      toast.error('Please enter shipper email');
      return;
    }
    
    setSendingTracking(true);
    try {
      const response = await api.post('/tracking/create', {
        load_id: selectedLoad.id,
        shipper_email: shipperEmail,
        shipper_name: shipperName || null
      });
      
      setTrackingInfo({
        has_tracking: true,
        tracking_url: response.data.tracking_url,
        shipper_email: shipperEmail
      });
      
      if (response.data.email_sent) {
        toast.success('Tracking link sent to shipper!');
      } else {
        toast.success('Tracking link created (email may be delayed)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tracking');
    } finally {
      setSendingTracking(false);
    }
  };

  const handleResendTracking = async () => {
    setSendingTracking(true);
    try {
      const response = await api.post(`/tracking/load/${selectedLoad.id}/resend`);
      if (response.data.email_sent) {
        toast.success('Tracking email resent!');
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to resend email');
    } finally {
      setSendingTracking(false);
    }
  };

  const copyTrackingLink = () => {
    if (trackingInfo?.tracking_url) {
      navigator.clipboard.writeText(trackingInfo.tracking_url);
      toast.success('Tracking link copied!');
    }
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'auto_transport':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400">🚗 Auto</span>;
      case 'hotshot':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">⚡ Hotshot</span>;
      case 'freight':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">📦 Freight</span>;
      default:
        return null;
    }
  };

  const getStatusBadge = (load) => {
    if (load.status === 'delivered') {
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">✓ Delivered</span>;
    }
    if (!load.assigned_driver_id) {
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-500/20 text-gray-400">Unassigned</span>;
    }
    switch (load.assignment_status) {
      case 'assigned':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400">Assigned</span>;
      case 'picked_up':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">Picked Up</span>;
      case 'in_transit':
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-400">In Transit</span>;
      default:
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-500/20 text-gray-400">{load.assignment_status}</span>;
    }
  };

  const availableDrivers = drivers.filter(d => d.status === 'available');
  const filteredLoads = getFilteredLoads();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 pt-20 flex items-center justify-center">
        <Card className="max-w-md border-[#3B82F6]/20 bg-[#0D1B2A]">
          <CardContent className="pt-6 text-center">
            <LayoutDashboard className="w-12 h-12 mx-auto text-[#3B82F6] mb-4" />
            <h2 className="text-xl font-bold mb-2">Sign In Required</h2>
            <p className="text-gray-400 mb-4">Please sign in to access the dispatch board.</p>
            <Button onClick={() => navigate('/login')}>Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-20 pb-12 px-3 sm:px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0D1B2A] flex items-center gap-2 sm:gap-3">
              <LayoutDashboard className="w-6 h-6 sm:w-8 sm:h-8 text-[#3B82F6]" />
              Dispatch Board
            </h1>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
              Manage load assignments and track delivery status
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="outline" onClick={fetchData} className="border-[#3B82F6]/30 flex-1 sm:flex-none">
              <RefreshCw className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button onClick={() => navigate('/tms/drivers')} className="bg-[#3B82F6] hover:bg-[#2563EB] flex-1 sm:flex-none">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" /> <span className="hidden sm:inline">Manage</span> Drivers
            </Button>
          </div>
        </div>

        {/* SOS Alert Panel - Shows active emergencies */}
        <div className="mb-6">
          <SOSAlertPanel />
        </div>

        {/* Stats Cards - Horizontal scroll on mobile */}
        {stats && (
          <div className="overflow-x-auto pb-2 mb-6 sm:mb-8 -mx-3 px-3 sm:mx-0 sm:px-0 mobile-scrollbar">
            <div className="flex sm:grid sm:grid-cols-6 gap-3 sm:gap-4 min-w-max sm:min-w-0 pr-6 sm:pr-0">
              <Card className="border-gray-200 bg-white shadow-sm min-w-[100px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Drivers</p>
                      <p className="text-lg sm:text-xl font-bold text-[#0D1B2A]">{stats.drivers.total}</p>
                      <p className="text-[10px] sm:text-xs text-green-600">{stats.drivers.available} avail</p>
                    </div>
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#3B82F6] flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white shadow-sm min-w-[90px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Active</p>
                      <p className="text-lg sm:text-xl font-bold text-[#0D1B2A]">{stats.loads.active}</p>
                    </div>
                    <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white shadow-sm min-w-[90px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Pending</p>
                      <p className="text-lg sm:text-xl font-bold text-yellow-600">{stats.loads.pending_assignment}</p>
                    </div>
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white shadow-sm min-w-[90px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Transit</p>
                      <p className="text-lg sm:text-xl font-bold text-purple-600">{stats.loads.in_transit}</p>
                    </div>
                    <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white shadow-sm min-w-[90px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase">Done</p>
                      <p className="text-lg sm:text-xl font-bold text-green-600">{stats.loads.delivered}</p>
                    </div>
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-white shadow-sm min-w-[110px] sm:min-w-0">
                <CardContent className="p-3 sm:pt-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[10px] sm:text-xs text-gray-500 uppercase whitespace-nowrap">Revenue</p>
                      <p className="text-lg sm:text-xl font-bold text-green-600">${stats.revenue.monthly.toLocaleString()}</p>
                    </div>
                    <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Main View Tabs */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setActiveTab('loads')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'loads'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-white text-gray-600 hover:text-[#0D1B2A] border border-gray-200'
            }`}
          >
            <Package className="w-4 h-4" />
            Loads
          </button>
          <button
            onClick={() => setActiveTab('map')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === 'map'
                ? 'bg-[#3B82F6] text-white'
                : 'bg-white text-gray-600 hover:text-[#0D1B2A] border border-gray-200'
            }`}
          >
            <Map className="w-4 h-4" />
            Live Map
          </button>
        </div>

        {activeTab === 'map' ? (
          <DriverLocationMap />
        ) : (
          <>
            {/* Filter Tabs - Horizontal scroll on mobile */}
            <div className="overflow-x-auto pb-2 mb-4 sm:mb-6 -mx-3 px-3 sm:mx-0 sm:px-0 mobile-scrollbar">
              <div className="flex items-center gap-2 min-w-max sm:min-w-0 pr-6 sm:pr-0">
                {[
                  { key: 'all', label: 'All', count: loads.length },
                  { key: 'unassigned', label: 'Unassigned', count: loads.filter(l => !l.assigned_driver_id && l.status === 'posted').length },
                  { key: 'assigned', label: 'Assigned', count: loads.filter(l => l.assignment_status === 'assigned').length },
                  { key: 'in_transit', label: 'Transit', count: loads.filter(l => ['picked_up', 'in_transit'].includes(l.assignment_status)).length },
                  { key: 'delivered', label: 'Done', count: loads.filter(l => l.status === 'delivered').length },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key)}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                      filter === tab.key
                        ? 'bg-[#3B82F6] text-white'
                        : 'bg-white text-gray-600 hover:text-[#0D1B2A] border border-gray-200'
                    }`}
                  >
                    {tab.label} <span className="ml-1 text-xs opacity-70">({tab.count})</span>
                  </button>
                ))}
              </div>
            </div>

        {/* Loads List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
          </div>
        ) : filteredLoads.length === 0 ? (
          <Card className="border-gray-200 bg-white shadow-sm">
            <CardContent className="py-16 text-center">
              <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-[#0D1B2A] mb-2">No Loads Found</h3>
              <p className="text-gray-500">
                {filter === 'all' ? 'No loads to display yet.' : `No ${filter} loads at the moment.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredLoads.map((load) => (
              <Card 
                key={load.id} 
                className={`border-gray-200 bg-white shadow-sm hover:border-[#3B82F6]/40 transition-all ${
                  load.status === 'delivered' ? 'opacity-70' : ''
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    {/* Load Info */}
                    <div className="flex items-center gap-6">
                      <div className="flex flex-col gap-1">
                        {getCategoryBadge(load.load_category)}
                        {getStatusBadge(load)}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 text-[#0D1B2A] font-medium">
                          <MapPin className="w-4 h-4 text-green-600" />
                          {load.pickup_location}
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                          <MapPin className="w-4 h-4 text-red-500" />
                          {load.delivery_location}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          Pickup: {new Date(load.pickup_date).toLocaleDateString()}
                          {load.vehicle_count && ` • ${load.vehicle_count} vehicle(s)`}
                          {load.weight && ` • ${load.weight.toLocaleString()} lbs`}
                        </div>
                      </div>
                    </div>

                    {/* Price & Driver */}
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-xl font-bold text-green-600">${load.price.toLocaleString()}</div>
                      </div>

                      {/* Driver Assignment */}
                      {load.assigned_driver_name ? (
                        <div className="flex items-center gap-3 bg-gray-100 rounded-lg px-4 py-2">
                          <div className="w-8 h-8 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
                            <Users className="w-4 h-4 text-[#3B82F6]" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-[#0D1B2A]">{load.assigned_driver_name}</div>
                            <div className="text-xs text-gray-500">Driver</div>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleOpenAssignDialog(load)}
                          disabled={availableDrivers.length === 0}
                          className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Assign Driver
                        </Button>
                      )}

                      {/* Status Actions */}
                      {load.assigned_driver_id && load.status !== 'delivered' && (
                        <div className="flex items-center gap-2">
                          {load.assignment_status === 'assigned' && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStatus(load.id, 'picked_up')}
                              className="bg-yellow-500 hover:bg-yellow-600 text-black"
                            >
                              <Play className="w-4 h-4 mr-1" /> Picked Up
                            </Button>
                          )}
                          {load.assignment_status === 'picked_up' && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStatus(load.id, 'in_transit')}
                              className="bg-purple-500 hover:bg-purple-600"
                            >
                              <Truck className="w-4 h-4 mr-1" /> In Transit
                            </Button>
                          )}
                          {['picked_up', 'in_transit'].includes(load.assignment_status) && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStatus(load.id, 'delivered')}
                              className="bg-green-500 hover:bg-green-600 text-black"
                            >
                              <Check className="w-4 h-4 mr-1" /> Delivered
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenTrackingDialog(load)}
                            className="border-[#3B82F6]/30 hover:bg-[#3B82F6]/10"
                          >
                            <Mail className="w-4 h-4 mr-1" /> Track
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUnassign(load.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      {/* Invoice button for delivered loads */}
                      {load.status === 'delivered' && (
                        <Button
                          size="sm"
                          onClick={() => navigate('/tms/invoices')}
                          className="bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                        >
                          <FileText className="w-4 h-4 mr-1" /> Invoice
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}

        {/* Assign Driver Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="bg-[#0D1B2A] border-[#3B82F6]/20 max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Driver</DialogTitle>
              <DialogDescription>
                Select a driver to assign to this load
              </DialogDescription>
            </DialogHeader>

            {selectedLoad && (
              <div className="p-4 bg-[#112240] rounded-lg mb-4">
                <div className="flex items-center gap-2 text-white font-medium">
                  <MapPin className="w-4 h-4 text-green-400" />
                  {selectedLoad.pickup_location}
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                  <MapPin className="w-4 h-4 text-red-400" />
                  {selectedLoad.delivery_location}
                </div>
                <div className="text-sm text-gray-400 mt-1">
                  ${selectedLoad.price.toLocaleString()} • {new Date(selectedLoad.pickup_date).toLocaleDateString()}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Select Driver</label>
                {availableDrivers.length === 0 ? (
                  <div className="p-4 text-center bg-[#112240] rounded-lg">
                    <AlertTriangle className="w-8 h-8 mx-auto text-yellow-400 mb-2" />
                    <p className="text-sm text-gray-400">No available drivers</p>
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => { setShowAssignDialog(false); navigate('/tms/drivers'); }}
                      className="mt-2"
                    >
                      Add a driver
                    </Button>
                  </div>
                ) : (
                  <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                    <SelectTrigger className="bg-[#0A1628] border-[#3B82F6]/20">
                      <SelectValue placeholder="Select a driver" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#112240] border-[#3B82F6]/20">
                      {availableDrivers.map(driver => (
                        <SelectItem key={driver.id} value={driver.id}>
                          <div className="flex items-center gap-2">
                            <span>{driver.first_name} {driver.last_name}</span>
                            {driver.equipment_type && (
                              <span className="text-xs text-gray-500">({driver.equipment_type.replace('_', ' ')})</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Send Driver App Invite Checkbox */}
              {selectedDriver && (
                <div className="flex items-center gap-3 p-3 bg-[#112240] rounded-lg">
                  <input
                    type="checkbox"
                    id="sendInvite"
                    checked={sendDriverInvite}
                    onChange={(e) => setSendDriverInvite(e.target.checked)}
                    className="w-5 h-5 rounded border-[#3B82F6]/30 bg-[#0A1628] text-[#4ADE80] focus:ring-[#4ADE80]"
                  />
                  <label htmlFor="sendInvite" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2 text-white font-medium">
                      <Mail className="w-4 h-4 text-[#60A5FA]" />
                      Send Driver App Invite
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Email & SMS invite to download the driver app
                    </p>
                  </label>
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignDriver}
                  disabled={assigning || !selectedDriver}
                  className="flex-1 bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                >
                  {assigning ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Assigning...</>
                  ) : (
                    'Assign Driver'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Send Tracking Dialog */}
        <Dialog open={showTrackingDialog} onOpenChange={setShowTrackingDialog}>
          <DialogContent className="bg-[#0D1B2A] border-[#3B82F6]/20 max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#4ADE80]" />
                Customer Tracking
              </DialogTitle>
              <DialogDescription>
                Send a tracking link to the shipper so they can monitor their shipment
              </DialogDescription>
            </DialogHeader>

            {selectedLoad && (
              <div className="p-4 bg-[#112240] rounded-lg mb-4">
                <div className="flex items-center gap-2 text-white font-medium">
                  <MapPin className="w-4 h-4 text-green-400" />
                  {selectedLoad.pickup_location}
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                  <MapPin className="w-4 h-4 text-red-400" />
                  {selectedLoad.delivery_location}
                </div>
              </div>
            )}

            <div className="space-y-4">
              {trackingInfo?.has_tracking ? (
                // Tracking already exists
                <div className="space-y-4">
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="flex items-center gap-2 text-green-400 font-medium mb-2">
                      <CheckCircle className="w-5 h-5" />
                      Tracking Link Active
                    </div>
                    <p className="text-sm text-gray-400 mb-3">
                      Sent to: {trackingInfo.shipper_email}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={copyTrackingLink}
                        className="flex-1"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Link
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(trackingInfo.tracking_url, '_blank')}
                        className="flex-1"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Preview
                      </Button>
                    </div>
                  </div>
                  <Button
                    onClick={handleResendTracking}
                    disabled={sendingTracking}
                    variant="outline"
                    className="w-full"
                  >
                    {sendingTracking ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" /> Resend Email</>
                    )}
                  </Button>
                </div>
              ) : (
                // Create new tracking
                <div className="space-y-4">
                  <div>
                    <Label>Shipper Email *</Label>
                    <Input
                      type="email"
                      value={shipperEmail}
                      onChange={(e) => setShipperEmail(e.target.value)}
                      placeholder="customer@example.com"
                      className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    />
                  </div>
                  <div>
                    <Label>Shipper Name (Optional)</Label>
                    <Input
                      value={shipperName}
                      onChange={(e) => setShipperName(e.target.value)}
                      placeholder="John Smith"
                      className="mt-1 bg-[#0A1628] border-[#3B82F6]/20"
                    />
                  </div>
                  <Button
                    onClick={handleSendTracking}
                    disabled={sendingTracking || !shipperEmail}
                    className="w-full bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                  >
                    {sendingTracking ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" /> Send Tracking Link</>
                    )}
                  </Button>
                </div>
              )}

              <Button
                variant="ghost"
                onClick={() => setShowTrackingDialog(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DispatchBoardPage;
